import { formatBytesToString, formatDateToLocale } from '../utils/units';
import type { ColumnDefinition } from './unfurl';

/**
 * Every unfurl source describes its own fields through `columnDefinitions` (key, type, display
 * name, `defaultHidden`, enum `choices`). These helpers turn those definitions into what the
 * import table shows, so a field the backend starts sending appears without a frontend change.
 */

export type UnfurlColumnValue = string | number | boolean | Date;

/** Anything carrying the definitions of the source it came from (an `UnfurlItem`). */
interface WithColumnDefinitions {
  columnDefinitions?: readonly ColumnDefinition[];
}

/**
 * The union of the definitions across items, in order of first appearance. Items from several
 * pasted links can mix sources, each with its own set. When two sources declare the same key, the
 * first one's name and type win, and the column is hidden by default only if every source that
 * declares it hides it.
 */
export function mergeColumnDefinitions(
  items: readonly WithColumnDefinitions[],
): ColumnDefinition[] {
  const merged = new Map<string, ColumnDefinition>();
  // Every item from one source shares that source's array, so a channel of thousands of items
  // costs one pass, not thousands.
  const seen = new Set<readonly ColumnDefinition[]>();
  for (const item of items) {
    const definitions = item.columnDefinitions;
    if (!definitions || seen.has(definitions)) continue;
    seen.add(definitions);
    for (const definition of definitions) {
      if (!definition?.key) continue;
      const existing = merged.get(definition.key);
      if (!existing) {
        merged.set(definition.key, { ...definition });
        continue;
      }
      existing.defaultHidden = Boolean(existing.defaultHidden && definition.defaultHidden);
      if (definition.choices?.length) {
        const known = new Set(existing.choices?.map((choice) => choice.key));
        existing.choices = [
          ...(existing.choices ?? []),
          ...definition.choices.filter((choice) => !known.has(choice.key)),
        ];
      }
    }
  }
  return [...merged.values()];
}

/**
 * The value of one column, coerced to the type its definition declares, or null when it is
 * missing or doesn't fit. Coercing matters: Facebook Ads declares `isActive` as boolean but sends
 * the strings "True" and "False".
 */
export function readColumnValue(
  definition: ColumnDefinition,
  raw: unknown,
): UnfurlColumnValue | null {
  if (raw === undefined || raw === null || raw === '') return null;
  switch (definition.type) {
    case 'number': {
      const value = typeof raw === 'string' ? Number(raw.trim()) : raw;
      return typeof value === 'number' && Number.isFinite(value) ? value : null;
    }
    case 'boolean': {
      if (typeof raw === 'boolean') return raw;
      const value = String(raw).trim().toLowerCase();
      if (value === 'true') return true;
      if (value === 'false') return false;
      return null;
    }
    case 'ISODateString': {
      if (typeof raw !== 'string' && typeof raw !== 'number') return null;
      const date = new Date(raw);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    default: {
      // string, enum, thumbnail, and any type added to the backend after this was written.
      if (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') {
        return String(raw);
      }
      if (Array.isArray(raw) && raw.every((part) => typeof part !== 'object' || part === null)) {
        const joined = raw.filter((part) => part !== null && part !== '').join(', ');
        return joined || null;
      }
      return null;
    }
  }
}

/** Formatters for keys whose number carries a unit the definition type can't express. */
const NUMBER_FORMATTERS: Record<string, (value: number) => string> = {
  fileSize: (value) => formatBytesToString(value, { roundType: 'none' }),
};

/** Display text for a value from `readColumnValue`; an em dash when there is none. */
export function formatColumnValue(
  definition: ColumnDefinition,
  value: UnfurlColumnValue | null,
): string {
  if (value === null) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value instanceof Date) return String(formatDateToLocale(value.getTime()));
  if (typeof value === 'number') {
    return NUMBER_FORMATTERS[definition.key]?.(value) ?? value.toLocaleString('en-US');
  }
  if (definition.type === 'enum') {
    return definition.choices?.find((choice) => choice.key === value)?.value ?? value;
  }
  return value;
}

/**
 * Whether a column backed by `key` starts visible: the backend declared it and didn't mark it
 * hidden. With no definitions at all (items built without an unfurl), `fallback` decides.
 */
export function isDeclaredVisible(
  definitions: readonly ColumnDefinition[],
  key: string,
  fallback: boolean,
): boolean {
  if (definitions.length === 0) return fallback;
  const definition = definitions.find((candidate) => candidate.key === key);
  return Boolean(definition && !definition.defaultHidden);
}

/**
 * Folds a visibility change from the table into the user's overrides. Only columns the user
 * actually toggled are kept, so a column whose default changes later (a new link adds a field)
 * still follows its default. A missing key means visible, as in the table.
 */
export function applyVisibilityChange(
  current: Readonly<Record<string, boolean>>,
  overrides: Readonly<Record<string, boolean>>,
  next: Readonly<Record<string, boolean>>,
): Record<string, boolean> {
  const updated = { ...overrides };
  for (const field of new Set([...Object.keys(current), ...Object.keys(next)])) {
    const visible = next[field] !== false;
    if (visible !== (current[field] !== false)) updated[field] = visible;
  }
  return updated;
}
